/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import Modal from "react-modal";
import CalendarItem from "./calendar-item";
import { useEffect, useState } from "react";
import { connectPersonalCalendar } from "apis/personal-calendar";
import { useToast } from "hook/useToast";

const modalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    border: "none",
    background: "transparent",
    transform: "translate(-50%, -50%)",
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};
Modal.setAppElement("#root");

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  onUpdate?: () => void;
  googleToken?: string;
  googleCalendarList?: { id: string; name: string }[] | null;
};

const GoogleCalendarModal = (props: Props) => {
  const { modalIsOpen, closeModal, onUpdate, googleCalendarList, googleToken } = props;
  const [selectedCalendar, setSelectedCalendar] = useState<{ name: string; id: string }>();
  const { showToast } = useToast();

  const onConnect = async () => {
    if (!selectedCalendar) return;
    else {
      const res = await connectPersonalCalendar(selectedCalendar.id, selectedCalendar.name, googleToken ?? "");
      if (res.success) {
        onUpdate?.();
        closeModal();
        showToast(`"${selectedCalendar.name}"이 연동되었습니다.`, "success");
      } else {
        showToast("캘린더 연동에 실패했습니다. 재시도해 주세요.", "error");
      }
    }
  };

  useEffect(() => {
    setSelectedCalendar(undefined);
  }, [modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={modalStyle}>
      <Container>
        <CancelIcon>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={20}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </CancelIcon>
        <TitleContainer>
          <UI.Icon name="google" size={24} />
          <UI.Typography type="title03" fontStyle="bold" color="var(--alias-color-label-neutral)">
            Google 개인 캘린더 연동
          </UI.Typography>
        </TitleContainer>
        <Content>
          <TopSection>
            {googleCalendarList && googleCalendarList.length === 0 && (
              <Information backgroundColor="#F1F2F3">
                <UI.Icon name="inform" size={24} color="var(--alias-color-label-alternative)" />
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  연동할 개인 캘린더가 없습니다. 캘린더 추가 후, 재시도해 주세요.
                </UI.Typography>
              </Information>
            )}
            {googleCalendarList && googleCalendarList.length > 0 && (
              <Information backgroundColor="var(--global-color-blue-98)">
                <UI.Icon name="inform" size={24} color="var(--alias-color-primary-normal)" />
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  연동할 캘린더를 선택해 주세요.
                </UI.Typography>
              </Information>
            )}
            {!googleCalendarList && (
              <Information backgroundColor="var(--global-color-red-95)">
                <UI.Icon name="inform" size={24} color="var(--alias-color-status-negative)" />
                <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
                  연동할 개인 캘린더를 불러오는 데 실패했습니다. 팝업을 닫고 재시도해주세요.
                </UI.Typography>
              </Information>
            )}
            <ListContainer>
              {googleCalendarList?.map((calendar, index) => (
                <CalendarItem
                  selected={selectedCalendar}
                  onClick={() => setSelectedCalendar(calendar)}
                  key={index}
                  name={calendar.name}
                />
              ))}
            </ListContainer>
          </TopSection>
          <UI.Button
            title="연동하기"
            size="large"
            style={{ width: "100%" }}
            onClick={onConnect}
            disabled={selectedCalendar ? false : true}
          />
        </Content>
      </Container>
    </Modal>
  );
};

export default GoogleCalendarModal;

const Container = styled.div`
  position: relative;
  display: flex;
  width: 600px;
  padding: 40px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;

  border-radius: 16px;
  background: white;
`;

const CancelIcon = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;

const TitleContainer = styled.div`
  display: flex;
  padding-bottom: 20px;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  border-bottom: 1px solid var(--alias-color-line-normal);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 240px;
  align-items: flex-start;
  gap: 8px;
  overflow-y: auto;
  width: 100%;

  ::-webkit-scrollbar {
    width: 12px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--component-color-fill-strong);
    border-radius: 8px;
  }
  ::-webkit-scrollbar-track {
    background-color: white;
  }
`;

const Information = styled.div<{ backgroundColor: string }>`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;

  border-radius: 8px;
  background: ${(props) => props.backgroundColor};
`;
